import { Box, Grid, Link, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

const rowSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 } as const;
const labelCellSx = { minWidth: 0 } as const;
// Values are short numbers; keep them on one line and let the label give way.
const valueSx = { flexShrink: 0, whiteSpace: 'nowrap' } as const;

interface LabelValueItem {
  label: string | ReactNode;
  value: string | ReactNode;
  tooltip?: string;
  // When set, the label renders as a button-link.
  onLabelClick?: () => void;
  valueSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
}

interface LabelValueGridProps {
  items: LabelValueItem[];
  spacing?: number;
  mt?: number;
}

export const LabelValueGrid = ({ items, spacing = 1, mt = 2 }: LabelValueGridProps) => {
  return (
    <Grid container rowSpacing={spacing} columns={2} mt={mt}>
      {items.map((item, index) => {
        const labelContent = item.label;
        return (
          <Grid key={index} size={2} sx={rowSx}>
            <Box sx={labelCellSx}>
              <Tooltip title={item.tooltip} arrow placement="top" disableInteractive>
                <Typography
                  textAlign="start"
                  variant="body2"
                  color="text.secondary"
                  sx={
                    [
                      (theme: Theme) => ({
                        ...(item.tooltip && theme.custom.helpText),
                      }),
                      ...(item.labelSx ? (Array.isArray(item.labelSx) ? item.labelSx : [item.labelSx]) : []),
                    ] as SxProps<Theme>
                  }
                >
                  {item.onLabelClick ? (
                    <Link
                      component="button"
                      type="button"
                      onClick={item.onLabelClick}
                      underline="hover"
                      sx={{ verticalAlign: 'baseline', fontSize: 'inherit', lineHeight: 'inherit' }}
                    >
                      {labelContent}
                    </Link>
                  ) : (
                    labelContent
                  )}
                </Typography>
              </Tooltip>
            </Box>
            <Typography
              textAlign="end"
              variant="body2"
              sx={[valueSx, ...(Array.isArray(item.valueSx) ? item.valueSx : [item.valueSx])] as SxProps<Theme>}
            >
              {item.value}
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
};
